<template>
    <div :class="$style.container">
        <div :class="$style.center">
            <img
                :class="$style.img"
                :src="content.img"
                :alt="content.imgAlt"
            />
            <div :class="$style.texts">
                <Typography
                    is="h2"
                    type="header"
                    size="m"
                >
                    {{ content.title }}
                </Typography>
                <Typography
                    type="paragraph"
                    size="s"
                >
                    {{ content.text }}
                </Typography>
            </div>
            <div
                v-if="showButtons"
                :class="$style.buttons"
            >
                <component
                    :is="index === 0 ? Button : OutlineButton"
                    v-for="(button, index) in content.buttons"
                    :key="index"
                    :class="$style.button_override"
                    size="l"
                    :interfaceTarget="button.interfaceTarget"
                    :to="button.link"
                    :label="button.text"
                />
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import Button from '@/components_new/Buttons/Button.vue'
import OutlineButton from '@/components_new/Buttons/OutlineButton.vue'
import { useTr } from '@/composables/use-tr'
import { ROUTE_NAMES } from '@/router/constants'
import { type PageStatus } from '@/store/app-store'

import Typography from '../Typography.vue'
import img404 from './assets/404.webp'
import imgJoystickOnFire from './assets/joystick-on-fire.webp'

const tr = useTr()

const props = defineProps<{
    type: Exclude<PageStatus, 200>
    noRandomGame?: boolean
    showButtons?: boolean
}>()

const route = useRoute()

const contentForType = computed(() => ({
    404: {
        img: img404,
        imgAlt: tr('errorContent.404_imgAlt'),
        title: tr('errorContent.404_title'),
        text: tr('errorContent.404_text'),
        buttons: [
            {
                text: tr('errorContent.404_back'),
                link: { name: ROUTE_NAMES.MAIN },
                interfaceTarget: 'navigation-link',
            },
            !props.noRandomGame && {
                text: tr('errorContent.404_randomGame'),
                interfaceTarget: 'random-game',
                link: { name: ROUTE_NAMES.RANDOM_GAME },
            },
        ].filter((x) => !!x),
    },
    500: {
        img: imgJoystickOnFire,
        imgAlt: tr('errorContent.500_imgAlt'),
        title: tr('errorContent.500_title'),
        text: tr('errorContent.500_text'),
        buttons: [
            {
                text: tr('errorContent.500_reload'),
                link: route.fullPath,
                interfaceTarget: 'reload-page',
            },
            {
                text: tr('errorContent.500_back'),
                link: { name: ROUTE_NAMES.MAIN },
                interfaceTarget: 'navigation-link',
            },
        ],
    },
}))

const content = computed(() => {
    if (props.type === 404) {
        return contentForType.value[404]
    }
    return contentForType.value[500]
})
</script>
<style module>
.container {
    flex-grow: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 60px 0;
}

.center {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 40px;
}

.img {
    max-width: 60%;
    max-height: 160px;
}

.texts {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.buttons {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}
.button_override {
    flex-grow: 1;
}
</style>
